import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { Controller, Control, FieldValues, FieldPath } from 'react-hook-form';

// @MUI Components
import Autocomplete from '@mui/material/Autocomplete';
import TextField, { StandardTextFieldProps } from '@mui/material/TextField';
import Skeleton from '@mui/material/Skeleton';
import CircularProgress from '@mui/material/CircularProgress';

// @Utils
import { axiosEstimates } from '../utils/api';

// @Interfaces and Types
import { ISelectInput, ITransportationNameRequest } from '../models';
interface Props<T extends FieldValues> extends StandardTextFieldProps {
  watcherValue?: number[];
  name: FieldPath<T>;
  control: Control<T, any>;
  loading?: boolean;
}

/**
 * @component
 * @subcategory Global
 *
 * @description
 * This component is a filter for transportation options. It uses the Autocomplete component from MUI and allows the user to search for transportation options by name. It also includes a loading state and error handling.
 * #### Example
 * ```jsx
 * <MultipleTransportationFilter name="transportation" control={control} watcherValue={[1, 2]} {...props} />
 * ```
 *
 * @property {FieldPath<T>} name The name of the field.
 * @property {boolean} [loading] A boolean indicating whether the input is loading.
 * @property {Control<T, any>} control The control object from react-hook-form.
 * @property {number[]} [watcherValue] The initial value of the filter.
 * @property {StandardTextFieldProps} props Additional props for the TextField component.
 *
 * @return {JSX.Element} The Autocomplete component with the specified props and functionality.
 * @author CML Exports Front-End Developers
 */
export default function MultipleTransportationFilter<T extends FieldValues>({
  name,
  loading,
  control,
  watcherValue,
  ...props
}: Props<T>) {
  const [options, setOptions] = useState<ISelectInput<number>[]>([]);
  const [selectOptions, setSelectOptions] = useState<number[]>([]);
  const [spinner, setSpinner] = useState<boolean>(false);
  const { t, i18n } = useTranslation(['common']);

  const handleFilter = async (value: string) => {
    setSpinner(true);
    try {
      const response = await axiosEstimates.get<ITransportationNameRequest>('transportation-by-name?name=' + value, {
        headers: { 'x-module': 'services' },
      });
      const items: ISelectInput<number>[] = response.data.getTransportation.map(type => ({
        label: type.name,
        labelEN: type.nameEN,
        value: type.id,
      }));

      // const newItem: ISelectInput<string>[] = [{ label: `${t('add.general')} ${value}`, value }];

      const newItems: ISelectInput<number>[] = [];

      for (const item of items) {
        if (!selectOptions.includes(item.value)) {
          newItems.push(item);
        }
      }

      setOptions(newItems);
      setSpinner(false);
    } catch (error) {
      setOptions([]);
      setSpinner(false);
    }
  };

  useEffect(() => {
    if (watcherValue) {
      setSelectOptions(watcherValue);
    }
    handleFilter('');
  }, [watcherValue]);

  return (
    <>
      {!loading ? (
        <Controller
          control={control}
          name={name}
          render={({ field: { value, onChange, ...otherField }, fieldState: { error } }) => (
            <Autocomplete
              freeSolo
              multiple
              autoHighlight
              loading={spinner}
              options={options}
              getOptionLabel={(option: any) => (i18n.language === 'es' ? option.label : option.labelEN)}
              value={value}
              onInputChange={async (event, value) => {
                handleFilter(value);
              }}
              onChange={(event, item: any) => {
                const ids = new Set();
                const newItems = item.filter((item: any) => {
                  if (ids.has(item.value)) {
                    return false;
                  }
                  ids.add(item.value);
                  return true;
                });

                setSelectOptions(newItems.map((item: { value: number }) => item.value));
                onChange(newItems);
                handleFilter('');
              }}
              {...otherField}
              renderInput={params => (
                <TextField
                  {...params}
                  {...props}
                  error={Boolean(error?.message)}
                  helperText={t(`${error?.message || ''}`)}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {spinner ? <CircularProgress color='inherit' size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
        />
      ) : (
        <Skeleton variant='rectangular' height={60} />
      )}
    </>
  );
}
